import { Link, useParams } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";

import AdminLayout from "../../layouts/AdminLayout";
import ManagerLayout from "../../layouts/ManagerLayout";
import EmployeeLayout from "../../layouts/EmployeeLayout";

import apiClient from "../../api/apiClient";

// ─── Timezone-safe date formatter ────────────────────────────────────────────
function formatDateDisplay(dateString) {
  if (!dateString) return "—";
  const parts = dateString.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    const [y, m, d] = parts.map(Number);
    const local = new Date(y, m - 1, d);
    return local.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
}

const MONTH_NAMES = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const MONTH_MAP = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December"
};

// ─── Reusable styled select ───────────────────────────────────────────────────
function FilterSelect({ label, value, onChange, children }) {
  return (
    <div className="flex flex-col gap-1 min-w-0 flex-1 sm:flex-none">
      {label && (
        <label className="text-white/60 text-[10px] sm:text-xs font-semibold uppercase tracking-wide px-0.5">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="
          w-full bg-white/20 border border-white/30 rounded-xl
          px-3 py-2 text-white text-xs sm:text-sm
          focus:outline-none focus:border-cyan-400
          transition appearance-none
          cursor-pointer
        "
        style={{ backgroundImage: "none" }}
      >
        {children}
      </select>
    </div>
  );
}

function ProjectProgress() {
  const { projectId } = useParams();
  const role = useAuthStore((state) => state.role);

  const Layout =
    role === "ADMIN" ? AdminLayout :
      role === "MANAGER" ? ManagerLayout :
        EmployeeLayout;

  const currentDate = new Date();
  const DEFAULT_MONTH = String(currentDate.getMonth() + 1).padStart(2, "0");
  const DEFAULT_YEAR = String(currentDate.getFullYear());

  const [projectName, setProjectName] = useState("");
  const [projectProgress, setProjectProgress] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedDisciplineIndex, setSelectedDisciplineIndex] = useState("all");
  const [selectedStageName, setSelectedStageName] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await apiClient.get(`/projects/api/projects/${projectId}/progress/`);
        setProjectName(res.data.project_name);
        setProjectProgress(res.data.project_progress || []);
        if (res.data.years) {
          setAvailableYears(res.data.years);
        }
      } catch (err) {
        console.error("Progress load failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [projectId]);

  // ── Safe current discipline ───────────────────────────────────────────────
  const currentDiscipline =
    selectedDisciplineIndex === "all"
      ? null
      : projectProgress[Number(selectedDisciplineIndex)];

  const availableStageNames = useMemo(() => {
    const types = new Set();
    const disciplinesToUse =
      selectedDisciplineIndex === "all"
        ? projectProgress
        : [projectProgress[Number(selectedDisciplineIndex)]];

    disciplinesToUse.forEach((discipline) => {
      if (discipline && discipline.stages) {
        discipline.stages.forEach((s) => {
          if (s.stage_name) {
            types.add(s.stage_name);
          }
        });
      }
    });
    return Array.from(types).sort();
  }, [projectProgress, selectedDisciplineIndex]);

  useEffect(() => {
    setSelectedStageName("all");
  }, [selectedDisciplineIndex]);

  // ── Filter data ───────────────────────────────────────────────────────────
  const getFilteredRows = () => {
    if (!projectProgress.length) return [];
    const disciplinesToUse =
      selectedDisciplineIndex === "all"
        ? projectProgress
        : [projectProgress[Number(selectedDisciplineIndex)]];

    const rows = [];
    disciplinesToUse.forEach((discipline) => {
      if (!discipline) return;
      discipline.days.forEach((day) => {
        const [year, month] = day.date.split("-");
        if (
          (selectedMonth === "all" || month === selectedMonth) &&
          (selectedYear === "all" || year === selectedYear)
        ) {
          day.employees.forEach((emp) => {
            if (selectedStageName === "all" || emp.stage_name === selectedStageName) {
              rows.push({
                employeeid: emp.employee_id,
                employee: emp.employee_name,
                date: day.date,
                loggedHours: emp.logged_hours,
                stageName: emp.stage_name || "—",
                description: emp.description || "—",
              });
            }
          });
        }
      });
    });
    return rows;
  };

  const filteredRows = getFilteredRows();

  const downloadExcel = () => {
    if (!filteredRows.length) {
      alert("No data available to download.");
      return;
    }

    const headers = ["Employee ID", "Employee", "Date", "Stage", "Description", "Activity Hrs"];
    const rows = filteredRows.map((row) => [
      row.employeeid,
      row.employee,
      formatDateDisplay(row.date),
      row.stageName,
      row.description,
      `${row.loggedHours} hrs`
    ]);

    const spacingRow = ["", "", "", "", "", ""];
    const summaryRow1 = ["Total Estimated Hours", `${totalEstimatedHours} hrs`, "", "", "", ""];
    const summaryRow2 = ["Total Activity Hours", `${totalActivityHours} hrs`, "", "", "", ""];
    const summaryRow3 = ["Progress Percentage", `${percent}%`, "", "", "", ""];

    const ws = XLSX.utils.aoa_to_sheet([
      headers,
      ...rows,
      spacingRow,
      summaryRow1,
      summaryRow2,
      summaryRow3
    ]);

    ws["!cols"] = [
      { wch: 15 }, // Employee ID
      { wch: 25 }, // Employee
      { wch: 15 }, // Date
      { wch: 20 }, // Stage
      { wch: 45 }, // Description
      { wch: 15 }, // Activity Hrs
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Project Progress");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    const blob = new Blob([s2ab(wbout)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const monthName = selectedMonth === "all" ? "AllMonths" : (MONTH_MAP[selectedMonth] || selectedMonth);
    const yearVal = selectedYear === "all" ? "AllYears" : selectedYear;
    link.setAttribute("download", `ProjectProgress_${projectId}_${monthName}_${yearVal}.xlsx`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalEstimatedHours = useMemo(() => {
    if (selectedDisciplineIndex !== "all") {
      return projectProgress[Number(selectedDisciplineIndex)]?.total_estimated_hours || 0;
    }
    return projectProgress.reduce((sum, discipline) => sum + (discipline.total_estimated_hours || 0), 0);
  }, [projectProgress, selectedDisciplineIndex]);

  const totalActivityHours = useMemo(() => {
    return filteredRows.reduce((sum, row) => sum + (row.loggedHours || 0), 0);
  }, [filteredRows]);

  // ── Progress bar ──────────────────────────────────────────────────────────
  const percent =
    totalEstimatedHours > 0
      ? Math.min(
        Math.round((totalActivityHours / totalEstimatedHours) * 100),
        100
      )
      : 0;

  const progressColor = useMemo(() => {
    if (totalEstimatedHours <= 0) return "bg-green-500";
    const ratio = totalActivityHours / totalEstimatedHours;
    if (ratio > 1.05) {
      return "bg-red-500";
    } else if (ratio >= 0.85) {
      return "bg-green-500";
    } else {
      return "bg-yellow-400";
    }
  }, [totalActivityHours, totalEstimatedHours]);

  // ── Available years ───────────────────────────────────────────────────────
  const years = useMemo(() => {
    const yearsSet = new Set(availableYears.map(String));
    projectProgress.forEach((discipline) => {
      if (discipline && discipline.days) {
        discipline.days.forEach((d) => {
          if (d.date) {
            const yr = d.date.split("-")[0];
            if (yr) yearsSet.add(yr);
          }
        });
      }
    });
    const currentYear = new Date().getFullYear().toString();
    yearsSet.add(currentYear);
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [availableYears, projectProgress]);


  // ── Active filter count (for mobile badge) ────────────────────────────────
  const activeFilters = [
    selectedDisciplineIndex !== "all",
    selectedStageName !== "all",
    selectedMonth !== DEFAULT_MONTH,
    selectedYear !== DEFAULT_YEAR,
  ].filter(Boolean).length;

  return (
    <Layout>
      <div className="p-3 sm:p-6">
        <div className="max-w-[1400px] mx-auto">

          {/* ── MAIN CARD ── */}
          <div className="bg-white/15 backdrop-blur-xl rounded-3xl shadow-2xl p-4 sm:p-8 border border-white/20">

            {/* TITLE BAR */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-center py-3 sm:py-4 rounded-2xl font-bold text-sm sm:text-xl mb-4 sm:mb-6 shadow-lg">
              Project Progress &amp; Time Sheet
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 gap-3">
                <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-white/70 text-sm">Loading...</span>
              </div>
            ) : (
              <>
                {/* ── SUMMARY CARDS ── */}
                <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-5 sm:mb-8">
                  <div className="bg-white/20 rounded-2xl p-4 sm:p-6 text-center border border-white/10 shadow-md">
                    <p className="text-white/70 text-xs sm:text-sm font-medium leading-tight uppercase tracking-wider">
                      Total Estimated Hours
                    </p>
                    <h2 className="text-lg sm:text-3xl font-extrabold text-white mt-2 flex items-center justify-center">
                      {totalEstimatedHours}
                      <span className="text-xs sm:text-lg font-normal opacity-70 ml-1">hrs</span>
                    </h2>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">
                      {selectedDisciplineIndex === "all" ? "Sum of all disciplines" : `Discipline: ${projectProgress[Number(selectedDisciplineIndex)]?.discipline_id}`}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 backdrop-blur-md rounded-2xl p-4 sm:p-6 text-center border border-white/15 shadow-md">
                    <p className="text-white/75 text-xs sm:text-sm font-medium leading-tight uppercase tracking-wider">
                      Total Activity Hours
                    </p>
                    <h2 className="text-lg sm:text-3xl font-extrabold text-cyan-300 mt-2 flex items-center justify-center">
                      {totalActivityHours}
                      <span className="text-xs sm:text-lg font-normal opacity-80 ml-1">hrs</span>
                    </h2>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">
                      Sum of filtered entries
                    </p>
                  </div>
                </div>

                {/* ── PROJECT INFO ROW ── */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                  {/* Project label */}
                  <div className="text-white text-sm sm:text-base">
                    <span className="text-white/60 text-xs sm:text-sm mr-1">Project ID:</span>
                    <strong>{projectId}</strong>
                    {projectName && (
                      <span className="text-white/50 ml-2 text-xs sm:text-sm">({projectName})</span>
                    )}
                  </div>

                  {/* ── DESKTOP FILTERS — all inline, hidden on mobile ── */}
                  <div className="hidden sm:flex flex-wrap gap-3 items-end">
                    <FilterSelect
                      label="Discipline"
                      value={selectedDisciplineIndex}
                      onChange={setSelectedDisciplineIndex}
                    >
                      <option value="all" className="bg-slate-800">All Disciplines</option>
                      {projectProgress.map((discipline, index) => (
                        <option key={discipline.discipline_id} value={index} className="bg-slate-800">
                          {discipline.discipline_id}
                        </option>
                      ))}
                    </FilterSelect>

                    <FilterSelect
                      label="Stages"
                      value={selectedStageName}
                      onChange={setSelectedStageName}
                    >
                      <option value="all" className="bg-slate-800">All Stages</option>
                      {availableStageNames.map((type) => (
                        <option key={type} value={type} className="bg-slate-800">
                          {type}
                        </option>
                      ))}
                    </FilterSelect>

                    <FilterSelect
                      label="Month"
                      value={selectedMonth}
                      onChange={setSelectedMonth}
                    >
                      <option value="all" className="bg-slate-800">All Months</option>
                      {MONTH_NAMES.map((m) => (
                        <option key={m.value} value={m.value} className="bg-slate-800">{m.label}</option>
                      ))}
                    </FilterSelect>

                    <FilterSelect
                      label="Year"
                      value={selectedYear}
                      onChange={setSelectedYear}
                    >
                      <option value="all" className="bg-slate-800">All Years</option>
                      {years.map((year) => (
                        <option key={year} value={year} className="bg-slate-800">{year}</option>
                      ))}
                    </FilterSelect>

                    <button
                      type="button"
                      onClick={downloadExcel}
                      className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all flex items-center gap-1.5 border border-emerald-400/20 h-[38px] self-end"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      Download Progress
                    </button>
                  </div>

                  {/* ── MOBILE: Filter toggle button ── */}
                  <button
                    onClick={() => setShowFilters((v) => !v)}
                    className="sm:hidden flex items-center gap-2 self-start px-4 py-2 rounded-xl bg-white/15 border border-white/25 text-white text-sm font-semibold transition hover:bg-white/25"
                  >
                    <span>⚙️ Filters</span>
                    {activeFilters > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-cyan-500 text-white text-[10px] font-bold min-w-[18px] text-center">
                        {activeFilters}
                      </span>
                    )}
                    <span className="text-white/50 text-xs ml-1">{showFilters ? "▲" : "▼"}</span>
                  </button>
                </div>

                {/* ── MOBILE FILTER PANEL — animated dropdown ── */}
                <motion.div
                  initial={false}
                  animate={showFilters ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className="sm:hidden overflow-hidden"
                >
                  <div className="bg-white/10 border border-white/20 rounded-2xl p-4 mb-4 space-y-3">
                    <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-1">
                      Filter Options
                    </p>

                    {/* Discipline */}
                    <div className="flex flex-col gap-1">
                      <label className="text-white/60 text-xs font-semibold uppercase tracking-wide">Discipline</label>
                      <select
                        value={selectedDisciplineIndex}
                        onChange={(e) => setSelectedDisciplineIndex(e.target.value)}
                        className="w-full bg-white/15 border border-white/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400 transition"
                      >
                        <option value="all" className="bg-slate-800">All Disciplines</option>
                        {projectProgress.map((discipline, index) => (
                          <option key={discipline.discipline_id} value={index} className="bg-slate-800">
                            {discipline.discipline_id}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Stages */}
                    <div className="flex flex-col gap-1">
                      <label className="text-white/60 text-xs font-semibold uppercase tracking-wide">Stages</label>
                      <select
                        value={selectedStageName}
                        onChange={(e) => setSelectedStageName(e.target.value)}
                        className="w-full bg-white/15 border border-white/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400 transition"
                      >
                        <option value="all" className="bg-slate-800">All Stages</option>
                        {availableStageNames.map((type) => (
                          <option key={type} value={type} className="bg-slate-800">
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Month + Year side by side */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-white/60 text-xs font-semibold uppercase tracking-wide">Month</label>
                        <select
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                          className="w-full bg-white/15 border border-white/30 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400 transition"
                        >
                          <option value="all" className="bg-slate-800">All</option>
                          {MONTH_NAMES.map((m) => (
                            <option key={m.value} value={m.value} className="bg-slate-800">{m.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-white/60 text-xs font-semibold uppercase tracking-wide">Year</label>
                        <select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                          className="w-full bg-white/15 border border-white/30 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400 transition"
                        >
                          <option value="all" className="bg-slate-800">All</option>
                          {years.map((year) => (
                            <option key={year} value={year} className="bg-slate-800">{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Quick-reset */}
                    <button
                      onClick={() => {
                        setSelectedDisciplineIndex("all");
                        setSelectedStageName("all");
                        setSelectedMonth(DEFAULT_MONTH);
                        setSelectedYear(DEFAULT_YEAR);
                        setShowFilters(false);
                      }}
                      className="w-full py-2 rounded-xl bg-white/10 border border-white/20 text-white/70 text-xs font-semibold hover:bg-white/20 transition"
                    >
                      Reset Filters
                    </button>

                    <button
                      type="button"
                      onClick={downloadExcel}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-xs shadow-md transition-all flex items-center justify-center gap-1.5 border border-emerald-400/20"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      Download Progress
                    </button>
                  </div>
                </motion.div>

                {/* ── RESULT COUNT PILL (mobile) ── */}
                <div className="sm:hidden flex items-center justify-between mb-3">
                  <p className="text-white/50 text-xs">
                    {filteredRows.length} record{filteredRows.length !== 1 ? "s" : ""} found
                  </p>
                  {activeFilters > 0 && (
                    <button
                      onClick={() => {
                        setSelectedDisciplineIndex("all");
                        setSelectedStageName("all");
                        setSelectedMonth(DEFAULT_MONTH);
                        setSelectedYear(DEFAULT_YEAR);
                      }}
                      className="text-cyan-400 text-xs font-semibold hover:text-cyan-300 transition"
                    >
                      Clear filters ✕
                    </button>
                  )}
                </div>

                {/* ── DESKTOP TABLE (sm+) ── */}
                <div className="hidden sm:block bg-white/10 rounded-2xl overflow-hidden border border-white/20 mb-8">
                  <table className="w-full text-sm text-center">
                    <thead className="bg-white/20">
                      <tr>
                        <th className="py-4 text-white px-4">Employee ID</th>
                        <th className="py-4 text-white px-4">Employee</th>
                        <th className="py-4 text-white px-4">Date</th>
                        <th className="py-4 text-white px-4">Stage</th>
                        <th className="py-4 text-white px-4">Description</th>
                        <th className="py-4 text-white px-4">Activity Hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="py-8 text-white/60">No records found</td>
                        </tr>
                      ) : (
                        filteredRows.map((row, i) => (
                          <motion.tr
                            key={i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="border-b border-white/10 hover:bg-white/10 transition"
                          >
                            <td className="py-3 px-4 text-white">{row.employeeid}</td>
                            <td className="py-3 px-4 text-white">{row.employee}</td>
                            <td className="py-3 px-4 text-white">{formatDateDisplay(row.date)}</td>
                            <td className="py-3 px-4 text-white">{row.stageName}</td>
                            <td className="py-3 px-4 text-white max-w-[250px] truncate" title={row.description}>{row.description}</td>
                            <td className="py-3 px-4 text-white">{row.loggedHours} hrs</td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── MOBILE CARDS (below sm) ── */}
                <div className="sm:hidden space-y-2.5 mb-5">
                  {filteredRows.length === 0 ? (
                    <div className="bg-white/10 rounded-2xl p-8 text-white/60 text-center text-sm">
                      <div className="text-3xl mb-2 opacity-40">📋</div>
                      No records found
                    </div>
                  ) : (
                    filteredRows.map((row, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(i * 0.03, 0.3) }}
                        className="bg-white/10 border border-white/20 rounded-xl overflow-hidden"
                      >
                        {/* Card header */}
                        <div className="flex items-center justify-between px-3 py-2.5 bg-white/10 border-b border-white/10">
                          <div className="min-w-0">
                            <p className="text-white font-semibold text-sm leading-tight truncate">{row.employee}</p>
                            <p className="text-white/50 text-xs">{row.employeeid}</p>
                          </div>
                        </div>
                        {/* Card data grid */}
                        <div className="grid grid-cols-3 divide-x divide-white/10">
                          <div className="p-2.5 text-center">
                            <p className="text-white/50 text-[10px] mb-0.5">Date</p>
                            <p className="text-white text-xs font-medium">{formatDateDisplay(row.date)}</p>
                          </div>
                          <div className="p-2.5 text-center">
                            <p className="text-white/50 text-[10px] mb-0.5">Stage</p>
                            <p className="text-white text-xs font-medium truncate">{row.stageName}</p>
                          </div>
                          <div className="p-2.5 text-center">
                            <p className="text-white/50 text-[10px] mb-0.5">Hours Logged</p>
                            <p className="text-white text-sm font-bold">{row.loggedHours}<span className="text-white/50 text-[10px] ml-0.5">h</span></p>
                          </div>
                        </div>
                        {/* Description */}
                        {row.description && row.description !== "—" && (
                          <div className="px-3 py-2 bg-white/5 border-t border-white/10 text-xs text-white/70">
                            <span className="text-white/40 font-semibold uppercase tracking-wide text-[9px] block">Description</span>
                            <p className="mt-0.5 break-words">{row.description}</p>
                          </div>
                        )}
                      </motion.div>
                    ))
                  )}
                </div>

                {/* ── PROGRESS BAR ── */}
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 sm:p-6 rounded-3xl mb-6 shadow-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
                    <h3 className="text-white font-bold text-sm sm:text-base tracking-wide">
                      Project/Discipline Progress
                    </h3>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-white/70">
                      <span>Logged: <strong>{totalActivityHours}h</strong></span>
                      <span className="opacity-40">|</span>
                      <span>Estimated: <strong>{totalEstimatedHours}h</strong></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-6">
                    <span className="text-white font-semibold text-xs sm:text-sm shrink-0 min-w-[70px]">
                      {selectedDisciplineIndex === "all" ? "All Disciplines" : currentDiscipline?.discipline_id}
                    </span>
                    <div className="flex-1 bg-white/10 h-3.5 sm:h-5 rounded-full overflow-hidden border border-white/5 relative">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percent}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className={`h-full rounded-full ${progressColor}`}
                      />
                    </div>
                    <span className="text-white font-bold text-xs sm:text-sm shrink-0 min-w-[38px] text-right">
                      {percent}%
                    </span>
                  </div>
                </div>

                {/* ── BACK LINK ── */}
                <div className="mt-4 sm:mt-6">
                  <Link
                    to={`/${role.toLowerCase()}/projects`}
                    className="inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm sm:text-base hover:underline transition"
                  >
                    ← Back to Projects
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ProjectProgress;
